import { AxiosError, GenericAbortSignal } from 'axios';
import { MentionItem } from 'react-mentions';

import { Paginated } from 'models/Paginated';
import http from 'services/http';

// A chat channel on an inbound referral: one conversation per facility the referral was
// sent to. `audience` is the facility group ('Group' in v1). `unreadCount` is CLIENT-side
// state: the server's list payload is pure channel metadata — the lean unread-counts
// endpoint is the sole counts source (OLIO-2834), so the fetcher seeds 0 and the
// useChatUnreadCounts merge is the only writer. The list is policy-scoped server-side, so
// it contains only channels the user is allowed to see.
export interface ChatConversation {
  id: string;
  audience: { id: string; name: string; type: string };
  unreadCount: number;
  // True while an unread message mentions the viewer — the rail badge's tag-blue variant.
  // Client-side state like unreadCount, merged by the same writer.
  unreadMentioned: boolean;
}

// A single row of the lean counts-only endpoint (OLIO-2834): the badge-tick / focus refetch
// transfers only these pairs, never the fixed channel metadata. Authoritative over the full
// visible set, so a badge that dropped to 0 is conveyed as a 0 row, not omitted.
export interface ChatUnreadCount {
  chatConversationId: string;
  unreadCount: number;
  mentioned: boolean;
}

// A message within a conversation. `text`/`plaintext` are the rich + stripped bodies (v1
// renders `plaintext`; mention chips arrive in OLIO-2837). `sentToCount` is null until a
// broadcast copy (OLIO-2836). The server orders newest-first; the client reverses for
// display (newest pinned to the bottom).
export interface ChatMessage {
  id: string;
  chatConversationId: string;
  text: string | null;
  plaintext: string;
  mentions: unknown[] | null;
  sentToCount: number | null;
  createdAt: string;
  createdBy: { id: string; name: string };
}

// The first page and every "load older" page fetch this many messages; a short page
// signals the end of history (the useInfiniteQuery termination rule).
export const MESSAGE_PAGE_SIZE = 50;

// Colocated query keys (the repo's `*QueryKeys` convention, mirroring workflowQueryKeys).
// `indexBase` is the prefix shared by every workflow's channel-list query, so a mutation that
// only knows a conversationId (mark-read) can patch the matching row across index queries
// without colliding with the counts query (a distinct 'unreadCounts' segment under the same base).
export const chatQueryKeys = {
  base: ['chatConversations'] as const,
  indexBase: ['chatConversations', 'index'] as const,
  index: (workflowId: string) => ['chatConversations', 'index', workflowId] as const,
  unreadCountsBase: ['chatConversations', 'unreadCounts'] as const,
  unreadCounts: (workflowId: string) => ['chatConversations', 'unreadCounts', workflowId] as const,
  messagesBase: ['chatMessages'] as const,
  messages: (conversationId: string) => ['chatMessages', 'index', conversationId] as const,
};

// The composer's working state for one channel (the mention kit's value shape). Held
// per-conversation by the page so unsent drafts — tags included — survive switches.
export type ChatDraft = { text: string; plaintext: string; mentions: MentionItem[] };
export const emptyChatDraft: ChatDraft = { text: '', plaintext: '', mentions: [] };

// The message body the composer sends: `text` carries the react-mentions markup, `plaintext`
// the stripped body. `broadcast` (OLIO-2836) and `mentions` are mutually exclusive; mentions
// are validated per-recipient server-side.
export type SendMessageInput = {
  text: string;
  plaintext: string;
  broadcast?: boolean;
  mentions?: MentionItem[];
};

// The per-recipient mention 422 — rendered inline by the composer (draft kept), never toasted.
export const MENTION_NOT_VISIBLE_ERROR = 'mention_not_visible';
export function isMentionNotVisibleError(error: unknown): boolean {
  return (error as AxiosError<{ error?: string }>)?.response?.data?.error === MENTION_NOT_VISIBLE_ERROR;
}

// Anchor-based "load older": the newest page omits `before`; each older page passes the
// composite cursor `<createdAt>|<id>` of the OLDEST loaded message as `cursor.lt` — the
// server composes the row-value predicate (created_at, id) < (?, ?), so a page boundary
// splitting same-timestamp messages drops nothing (timestamp-only anchors gap there).
// The dotted key is already snake_case, so OliveBranch leaves it untouched.
export async function getChatMessages(
  conversationId: string,
  { before, pageSize = MESSAGE_PAGE_SIZE }: { before?: string; pageSize?: number },
  signal?: GenericAbortSignal
): Promise<Paginated<ChatMessage>> {
  return http
    .get<Paginated<ChatMessage>>(`/chat_conversations/${conversationId}/messages`, {
      params: {
        sort_by: 'created_at desc',
        page_size: pageSize,
        ...(before ? { 'cursor.lt': before } : {}),
      },
      signal,
    })
    .then((res) => res.data);
}

// POST a message; the response is the persisted copy the caller appends to the cache
// (write-response-as-truth — no optimistic insert, per useWorkflow.ts).
export async function sendChatMessage(conversationId: string, body: SendMessageInput): Promise<ChatMessage> {
  return http
    .post<{ data: ChatMessage }>(`/chat_conversations/${conversationId}/messages`, body)
    .then((res) => res.data.data);
}

// Bare top-level resource keyed by a (chattableType, chattableId) pivot, mirroring the
// attachments endpoint. OliveBranch snake-cases the query params server-side.
export async function getChatConversations(
  workflowId: string,
  signal?: GenericAbortSignal
): Promise<ChatConversation[]> {
  return (
    http
      .get<{ data: Omit<ChatConversation, 'unreadCount' | 'unreadMentioned'>[] }>('/chat_conversations', {
        params: { chattableType: 'Workflow', chattableId: workflowId },
        signal,
      })
      // Counts are not part of the list payload — seed 0; useChatUnreadCounts owns the real
      // numbers (its mount fetch races this one, so badges land within a frame either way).
      .then((res) => res.data.data.map((conversation) => ({ ...conversation, unreadCount: 0, unreadMentioned: false })))
  );
}

// The counts snapshot behind the badge ledger: same bare pivot as the channel list, but only
// [{id, count}] pairs plus servedAt — the server-side moment the counts were computed, which
// the ledger uses to sequence buffered activity events against the snapshot.
export async function getChatUnreadCounts(
  workflowId: string,
  signal?: GenericAbortSignal
): Promise<{ servedAt: string; data: ChatUnreadCount[] }> {
  return http
    .get<{ servedAt: string; data: ChatUnreadCount[] }>('/chat_unread_counts', {
      params: { chattableType: 'Workflow', chattableId: workflowId },
      signal,
    })
    .then((res) => res.data);
}

// Mark a channel read at the newest message the client actually rendered (never "now"): a
// message that arrived after the last fetch stays unread (client-cursor semantics). The server
// clamps to <= now and only moves the cursor forward. OliveBranch snake-cases `lastSeenAt`.
export async function markChatConversationRead(conversationId: string, lastSeenAt: string): Promise<void> {
  await http.post(`/chat_conversations/${conversationId}/read`, { lastSeenAt });
}
